// v1.0.0 - 2026-02-02 - Energy Data Browser with filters and inline editing

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/stores/authStore'
import { useQuery, useMutation } from '@/lib/hooks'
import { cer, energyHourly } from '@/lib/api/client'
import type { EnergyHourlyItem } from '@/lib/api/client'
import { PageContainer, PageHeader } from '@/components/layout'
import { Card, CardContent, CardHeader, CardTitle, Button, Spinner, Badge } from '@/components/ui'
import { EditableCell } from '@/components/admin'

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debouncedValue
}

// Singleton formatters
const numberFormatter = new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
})

export default function EnergyDataBrowser() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuthStore()

  // Filter state from URL
  const [configId, setConfigId] = useState(searchParams.get('configId') || '')
  const [memberId, setMemberId] = useState(searchParams.get('memberId') || '')
  const [year, setYear] = useState(searchParams.get('year') || '')
  const [month, setMonth] = useState(searchParams.get('month') || '')
  const [type, setType] = useState<'' | 'immessa' | 'prelevata'>(
    (searchParams.get('type') as '' | 'immessa' | 'prelevata') || ''
  )
  const [podSearch, setPodSearch] = useState(searchParams.get('pod') || '')
  const [offset, setOffset] = useState(0)
  const limit = 100

  // Debounce POD search
  const debouncedPodSearch = useDebounce(podSearch, 300)

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    if (configId) params.set('configId', configId)
    if (memberId) params.set('memberId', memberId)
    if (year) params.set('year', year)
    if (month) params.set('month', month)
    if (type) params.set('type', type)
    if (debouncedPodSearch) params.set('pod', debouncedPodSearch)
    setSearchParams(params, { replace: true })
  }, [configId, memberId, year, month, type, debouncedPodSearch, setSearchParams])

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0)
  }, [configId, memberId, year, month, type, debouncedPodSearch])

  // Fetch configurations for filter dropdown
  const fetchConfigurations = useCallback(async () => {
    if (!user?.cer_id) return null
    const result = await cer.getConfigurations(user.cer_id)
    return result.configurations
  }, [user?.cer_id])

  const { data: configurations } = useQuery(fetchConfigurations, {
    enabled: !!user?.cer_id,
  })

  // Fetch members for filter dropdown
  const fetchMembers = useCallback(async () => {
    if (!user?.cer_id) return null
    const result = await cer.getMembers(user.cer_id, { limit: 200 })
    return result.members.map((m) => ({ id: m.id, name: m.name }))
  }, [user?.cer_id])

  const { data: members } = useQuery(fetchMembers, {
    enabled: !!user?.cer_id,
  })

  // Fetch energy data
  const fetchEnergyData = useCallback(async () => {
    if (!user?.cer_id) return null
    return cer.getEnergyDataBrowser(user.cer_id, {
      configId: configId || undefined,
      memberId: memberId || undefined,
      year: year ? parseInt(year, 10) : undefined,
      month: month ? parseInt(month, 10) : undefined,
      type: type || undefined,
      pod: debouncedPodSearch || undefined,
      offset,
      limit,
    })
  }, [user?.cer_id, configId, memberId, year, month, type, debouncedPodSearch, offset, limit])

  const {
    data: energyData,
    isLoading,
    error,
    refetch,
  } = useQuery(fetchEnergyData, {
    enabled: !!user?.cer_id,
  })

  // Update mutation
  const updateMutation = useMutation(async ({ id, kwh }: { id: string; kwh: number }) => {
    return energyHourly.update(id, kwh)
  })

  // Handle cell save
  const handleCellSave = useCallback(
    async (id: string, newValue: number) => {
      await updateMutation.mutate({ id, kwh: newValue })
      refetch()
    },
    [updateMutation, refetch]
  )

  // Generate year options (last 5 years)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 5 }, (_, i) => currentYear - i)
  }, [])

  // Month options
  const monthOptions = [
    { value: '1', label: 'Gennaio' },
    { value: '2', label: 'Febbraio' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Aprile' },
    { value: '5', label: 'Maggio' },
    { value: '6', label: 'Giugno' },
    { value: '7', label: 'Luglio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Settembre' },
    { value: '10', label: 'Ottobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Dicembre' },
  ]

  // Check if user is admin
  const isAdmin = user?.role === 'admin'

  // Group data by day for display
  const groupedData = useMemo(() => {
    if (!energyData?.items) return []

    const groups = new Map<string, EnergyHourlyItem[]>()
    for (const item of energyData.items) {
      const key = `${item.year}-${item.month}-${item.day}-${item.pod}-${item.type}`
      const existing = groups.get(key) || []
      existing.push(item)
      groups.set(key, existing)
    }

    return Array.from(groups.entries()).map(([_, items]) => {
      const first = items[0]
      const hourlyData = new Map(items.map((i) => [i.hour, i]))
      const total = items.reduce((sum, i) => sum + i.kwh, 0)
      return {
        ...first,
        hourlyData,
        total,
      }
    })
  }, [energyData?.items])

  return (
    <PageContainer>
      <PageHeader
        title="Browser Dati Energia"
        description="Visualizza e modifica i dati energetici orari"
        actions={
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Dashboard
          </Button>
        }
      />

      <div className="space-y-6">
        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filtri</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
              {/* Configuration */}
              <div>
                <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 block">
                  Configurazione
                </label>
                <select
                  value={configId}
                  onChange={(e) => setConfigId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-[var(--color-bg-primary)] text-sm"
                >
                  <option value="">Tutte</option>
                  {configurations?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.crin}
                    </option>
                  ))}
                </select>
              </div>

              {/* Member */}
              <div>
                <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 block">
                  Membro
                </label>
                <select
                  value={memberId}
                  onChange={(e) => setMemberId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-[var(--color-bg-primary)] text-sm"
                >
                  <option value="">Tutti</option>
                  {members?.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div>
                <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 block">
                  Anno
                </label>
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-[var(--color-bg-primary)] text-sm"
                >
                  <option value="">Tutti</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {/* Month */}
              <div>
                <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 block">
                  Mese
                </label>
                <select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-[var(--color-bg-primary)] text-sm"
                >
                  <option value="">Tutti</option>
                  {monthOptions.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 block">
                  Tipo
                </label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as '' | 'immessa' | 'prelevata')}
                  className="w-full px-3 py-2 border rounded-md bg-[var(--color-bg-primary)] text-sm"
                >
                  <option value="">Tutti</option>
                  <option value="immessa">Immessa</option>
                  <option value="prelevata">Prelevata</option>
                </select>
              </div>

              {/* POD Search */}
              <div>
                <label className="text-sm font-medium text-[var(--color-text-secondary)] mb-1 block">
                  Cerca POD
                </label>
                <input
                  type="text"
                  value={podSearch}
                  onChange={(e) => setPodSearch(e.target.value)}
                  placeholder="IT001E..."
                  className="w-full px-3 py-2 border rounded-md bg-[var(--color-bg-primary)] text-sm"
                />
              </div>
            </div>

            {/* Active filters summary */}
            {(configId || memberId || year || month || type || debouncedPodSearch) && (
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-[var(--color-text-secondary)]">Filtri attivi:</span>
                {configId && (
                  <Badge variant="default" className="text-xs">
                    Config: {configurations?.find((c) => c.id === configId)?.crin}
                  </Badge>
                )}
                {memberId && (
                  <Badge variant="default" className="text-xs">
                    Membro: {members?.find((m) => m.id === memberId)?.name}
                  </Badge>
                )}
                {year && <Badge variant="default" className="text-xs">Anno: {year}</Badge>}
                {month && (
                  <Badge variant="default" className="text-xs">
                    Mese: {monthOptions.find((m) => m.value === month)?.label}
                  </Badge>
                )}
                {type && (
                  <Badge variant={type === 'immessa' ? 'success' : 'info'} className="text-xs">
                    {type === 'immessa' ? 'Immessa' : 'Prelevata'}
                  </Badge>
                )}
                {debouncedPodSearch && (
                  <Badge variant="default" className="text-xs">POD: {debouncedPodSearch}</Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setConfigId('')
                    setMemberId('')
                    setYear('')
                    setMonth('')
                    setType('')
                    setPodSearch('')
                  }}
                  className="text-xs"
                >
                  Resetta filtri
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Dati Energia
                {energyData && (
                  <span className="ml-2 text-sm font-normal text-[var(--color-text-secondary)]">
                    ({energyData.total} record totali)
                  </span>
                )}
              </CardTitle>
              {isAdmin && (
                <Badge variant="warning" className="text-xs">
                  Modalita Admin: click per modificare
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : error ? (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">
                Errore nel caricamento dei dati: {error}
              </div>
            ) : groupedData.length === 0 ? (
              <div className="text-center py-12 text-[var(--color-text-secondary)]">
                Nessun dato trovato con i filtri selezionati
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="px-2 py-2 text-left font-medium">POD</th>
                        <th className="px-2 py-2 text-left font-medium">Membro</th>
                        <th className="px-2 py-2 text-left font-medium">Data</th>
                        <th className="px-2 py-2 text-left font-medium">Tipo</th>
                        {/* Hour columns */}
                        {Array.from({ length: 24 }, (_, i) => (
                          <th key={i} className="px-1 py-2 text-right font-medium text-xs w-12">
                            H{i + 1}
                          </th>
                        ))}
                        <th className="px-2 py-2 text-right font-medium">Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedData.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/50">
                          <td className="px-2 py-2 font-mono text-xs">{row.pod}</td>
                          <td className="px-2 py-2 truncate max-w-[150px]" title={row.memberName}>
                            {row.memberName}
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            {row.day}/{row.month}/{row.year}
                          </td>
                          <td className="px-2 py-2">
                            <Badge
                              variant={row.type === 'immessa' ? 'success' : 'info'}
                              className="text-xs"
                            >
                              {row.type === 'immessa' ? 'I' : 'P'}
                            </Badge>
                          </td>
                          {/* Hour cells */}
                          {Array.from({ length: 24 }, (_, hour) => {
                            const item = row.hourlyData.get(hour)
                            return (
                              <td key={hour} className="px-0 py-1">
                                {item ? (
                                  <EditableCell
                                    value={item.kwh}
                                    onSave={(newValue) => handleCellSave(item.id, newValue)}
                                    isEditable={isAdmin}
                                    className="text-xs"
                                  />
                                ) : (
                                  <span className="block text-right text-xs text-gray-300 px-2">-</span>
                                )}
                              </td>
                            )
                          })}
                          <td className="px-2 py-2 text-right font-semibold">
                            {numberFormatter.format(row.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {energyData && energyData.total > limit && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-[var(--color-text-secondary)]">
                      Mostrando {offset + 1}-{Math.min(offset + groupedData.length, energyData.total)} di{' '}
                      {energyData.total} record
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setOffset(Math.max(0, offset - limit))}
                        disabled={offset === 0}
                      >
                        Precedente
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setOffset(offset + limit)}
                        disabled={!energyData.hasMore}
                      >
                        Successivo
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  )
}
